import { useMutation } from '@tanstack/react-query';
import { toaster } from 'components/v2/toast';
import { useCallback, useState } from 'react';
import { searchCertificationBodies, searchIndividualVerifiers } from 'services/api/verifiersApi';
import { PoorMansError } from 'util/utils';
import {
  CertificationBodyListItem,
  CertificationBodySearchRequest,
  IndividualVerifierListItem,
  IndividualVerifierSearchRequest,
} from 'types/verifiers/VerifierModels';

export function useIndividualVerifierSearch() {
  const [isLoading, setIsLoading] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<IndividualVerifierSearchRequest | null>(null);
  const [searchResult, setSearchResult] = useState<{
    items: IndividualVerifierListItem[];
    totalCount: number;
  } | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const { mutateAsync: fetchVerifiers } = useMutation({
    mutationFn: searchIndividualVerifiers,
  });

  const onSearch = useCallback(
    async (formValues: Omit<IndividualVerifierSearchRequest, 'pageNumber' | 'pageSize'>) => {
      const request: IndividualVerifierSearchRequest = {
        ...formValues,
        pageNumber: 1,
        pageSize: pagination.pageSize,
      };
      try {
        setIsLoading(true);
        const result = await fetchVerifiers(request);
        setSearchResult({ items: result.items as IndividualVerifierListItem[], totalCount: result.totalCount });
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setCurrentRequest(request);
      } catch (error) {
        toaster({
          severity: 'error',
          summary: 'Error occurred',
          details: PoorMansError(error),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [fetchVerifiers, pagination.pageSize]
  );

  const onPageChange = useCallback(
    async ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => {
      if (!currentRequest) return;

      const nextRequest: IndividualVerifierSearchRequest = {
        ...currentRequest,
        pageNumber: pageIndex + 1,
        pageSize,
      };

      try {
        setIsLoading(true);
        const result = await fetchVerifiers(nextRequest);
        setSearchResult({ items: result.items as IndividualVerifierListItem[], totalCount: result.totalCount });
        setPagination({ pageIndex, pageSize });
        setCurrentRequest(nextRequest);
      } catch (error) {
        toaster({
          severity: 'error',
          summary: 'Error occurred',
          details: PoorMansError(error),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [currentRequest, fetchVerifiers]
  );

  return {
    isLoading,
    searchResult,
    onSearch,
    pagination,
    onPageChange,
  };
}

export function useCertificationBodySearch() {
  const [isLoading, setIsLoading] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<CertificationBodySearchRequest | null>(null);
  const [searchResult, setSearchResult] = useState<{
    items: CertificationBodyListItem[];
    totalCount: number;
  } | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const { mutateAsync: fetchBodies } = useMutation({
    mutationFn: searchCertificationBodies,
  });

  const onSearch = useCallback(
    async (formValues: Omit<CertificationBodySearchRequest, 'pageNumber' | 'pageSize'>) => {
      const request: CertificationBodySearchRequest = {
        ...formValues,
        pageNumber: 1,
        pageSize: pagination.pageSize,
      };
      try {
        setIsLoading(true);
        const result = await fetchBodies(request);
        setSearchResult({ items: result.items as CertificationBodyListItem[], totalCount: result.totalCount });
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setCurrentRequest(request);
      } catch (error) {
        toaster({
          severity: 'error',
          summary: 'Error occurred',
          details: PoorMansError(error),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [fetchBodies, pagination.pageSize]
  );

  const onPageChange = useCallback(
    async ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => {
      if (!currentRequest) return;

      const nextRequest: CertificationBodySearchRequest = {
        ...currentRequest,
        pageNumber: pageIndex + 1,
        pageSize,
      };

      try {
        setIsLoading(true);
        const result = await fetchBodies(nextRequest);
        setSearchResult({ items: result.items as CertificationBodyListItem[], totalCount: result.totalCount });
        setPagination({ pageIndex, pageSize });
        setCurrentRequest(nextRequest);
      } catch (error) {
        toaster({
          severity: 'error',
          summary: 'Error occurred',
          details: PoorMansError(error),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [currentRequest, fetchBodies]
  );

  return {
    isLoading,
    searchResult,
    onSearch,
    pagination,
    onPageChange,
  };
}
